import { zodResolver } from '@hookform/resolvers/zod'
import { type CourseFormInput, CourseFormSchema } from '@lms/shared'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { LessonOrderEditor } from '@/components/teacher/LessonOrderEditor'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { type LevelTagCombination, LevelTagSelector } from '@/components/ui/level-tag-selector'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useCourse, useCreateCourse, useUpdateCourse } from '@/hooks/use-courses'
import { useCreateTag, useTags } from '@/hooks/use-tags'
import { useTenantPath } from '@/lib/tenant'

export default function CourseFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const tp = useTenantPath()
  const isEdit = !!id

  const { data: course, isLoading } = useCourse(id!)
  const { data: tagsData } = useTags()
  const createCourse = useCreateCourse()
  const updateCourse = useUpdateCourse()
  const createTag = useCreateTag()

  const [combinations, setCombinations] = useState<LevelTagCombination[]>([])
  const [evaluationPrompt, setEvaluationPrompt] = useState<string>('')

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CourseFormInput>({
    resolver: zodResolver(CourseFormSchema),
    defaultValues: {
      title: '',
      description: '',
      levelTagCombinations: [],
      duration: '',
      isPublished: false,
    },
  })

  // combinations state を form に同期
  useEffect(() => {
    setValue('levelTagCombinations', combinations)
  }, [combinations, setValue])

  useEffect(() => {
    if (course && isEdit) {
      const c = course as any
      reset({
        title: c.title,
        description: c.description ?? '',
        duration: c.duration ?? '',
        isPublished: c.isPublished ?? false,
        levelTagCombinations: [],
      })
      // 既存データから combinations を復元
      if (c.tagIds && c.level) {
        const restored: LevelTagCombination[] = c.tagIds.map((tagId: string) => ({
          id: Math.random().toString(36).substr(2, 9),
          tagId,
          level: c.level,
        }))
        setCombinations(restored)
      }
      setEvaluationPrompt(c.evaluationPrompt ?? '')
    }
  }, [course, isEdit, reset])

  const availableTags = (Array.isArray(tagsData) ? tagsData : []).map((t: any) => ({
    id: t.id,
    name: t.name,
  }))

  const handleCreateTag = async (name: string) => {
    const tag = (await createTag.mutateAsync({ name })) as any
    return { id: tag.id, name: tag.name }
  }

  const onSubmit = async (data: CourseFormInput) => {
    try {
      // フラット変換: combinations → level + tagIds
      const level = data.levelTagCombinations[0]?.level ?? 'ENTRY'
      const tagIds = [...new Set(data.levelTagCombinations.map((c) => c.tagId))]

      const { levelTagCombinations: _, ...rest } = data
      const payload = {
        ...rest,
        level,
        tagIds: tagIds.length > 0 ? tagIds : undefined,
        evaluationPrompt: evaluationPrompt.trim() || null,
      }

      if (isEdit) {
        await updateCourse.mutateAsync({ id: id!, data: payload })
        toast.success('コースを更新しました')
      } else {
        await createCourse.mutateAsync(payload)
        toast.success('コースを作成しました')
      }
      navigate(tp('/teacher/courses'))
    } catch {
      toast.error(isEdit ? '更新に失敗しました' : '作成に失敗しました')
    }
  }

  if (isEdit && isLoading) {
    return <Skeleton className="h-96" />
  }

  return (
    <div className="max-w-2xl">
      <h1 className="mb-6 text-2xl font-bold">{isEdit ? 'コース編集' : 'コース作成'}</h1>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>基本情報</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">タイトル</Label>
              <Input id="title" placeholder="例: React入門講座" {...register('title')} />
              {errors.title && (
                <p className="text-sm text-destructive">{errors.title.message as string}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">説明</Label>
              <Textarea
                id="description"
                rows={5}
                placeholder="コースの概要を入力してください"
                {...register('description')}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description.message as string}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="duration">学習期間の目安</Label>
              <Input id="duration" placeholder="例: 2週間" {...register('duration')} />
              {errors.duration && (
                <p className="text-sm text-destructive">{errors.duration.message as string}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>タグ・レベル</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <LevelTagSelector
              value={combinations}
              onChange={setCombinations}
              availableTags={availableTags}
              onCreateTag={handleCreateTag}
            />
            {errors.levelTagCombinations && (
              <p className="text-sm text-destructive">
                {errors.levelTagCombinations.message as string}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              選択したタグとレベルの組み合わせに一致するレッスンが自動的にコースに紐付きます
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>公開設定</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <Switch
                id="isPublished"
                checked={watch('isPublished')}
                onCheckedChange={(checked) => setValue('isPublished', checked)}
              />
              <Label htmlFor="isPublished">公開する</Label>
            </div>
          </CardContent>
        </Card>

        {isEdit && (
          <Card>
            <CardHeader>
              <CardTitle>評価の観点（任意）</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                rows={3}
                placeholder="例: わかりやすかった点に着目して評価してください"
                value={evaluationPrompt}
                onChange={(e) => setEvaluationPrompt(e.target.value)}
                maxLength={200}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                設定すると、学習者の星評価フォームの上に案内文として表示されます（最大200文字）
              </p>
            </CardContent>
          </Card>
        )}

        <div className="flex gap-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? '保存中...' : isEdit ? '更新' : '作成'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(tp('/teacher/courses'))}>
            キャンセル
          </Button>
        </div>
      </form>

      {isEdit && id && (
        <div className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>レッスンの並び順</CardTitle>
            </CardHeader>
            <CardContent>
              <LessonOrderEditor courseId={id} />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
